interface TokenizerState {
  tokenList: string[]
  current: string
  depth: number
  inQuote: boolean
  quoteChar: string
}

const createInitialState = (): TokenizerState => ({
  tokenList: [],
  current: '',
  depth: 0,
  inQuote: false,
  quoteChar: '',
})

const processQuotedChar = (state: TokenizerState, ch: string, prevCh: string): boolean => {
  if (!state.inQuote) return false

  state.current += ch
  if (ch === state.quoteChar && prevCh !== '\\') {
    state.inQuote = false
  }
  return true
}

const processSpecialChar = (state: TokenizerState, ch: string): boolean => {
  if (ch === '"' || ch === "'") {
    state.inQuote = true
    state.quoteChar = ch
    state.current += ch
    return true
  }

  if (ch === '(') {
    state.depth++
    state.current += ch
    return true
  }

  if (ch === ')') {
    state.depth--
    state.current += ch
    return true
  }

  if (ch === ':' && state.depth === 0) {
    state.tokenList.push(state.current.trim())
    state.current = ''
    return true
  }

  return false
}

/**
 * Tokenizes the inner content of a variable block by splitting on `:` while
 * respecting parenthesized and quoted groups.
 */
export const tokenizeVariableContent = (raw: string): string[] => {
  const state = createInitialState()

  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]
    const prevCh = i > 0 ? raw[i - 1] : ''

    if (processQuotedChar(state, ch, prevCh)) continue
    if (processSpecialChar(state, ch)) continue

    state.current += ch
  }

  if (state.current.trim()) {
    state.tokenList.push(state.current.trim())
  }

  return state.tokenList
}

/**
 * Extracts the value inside parentheses from a token like `min(10)` or `label("Field")`.
 * Strips surrounding quotes from the value.
 */
export const extractParenValue = (token: string): string | null => {
  const openIdx = token.indexOf('(')
  if (openIdx === -1) return null

  const closeIdx = token.lastIndexOf(')')
  if (closeIdx === -1) return null

  const inner = token.slice(openIdx + 1, closeIdx).trim()
  const isQuoted =
    (inner.startsWith('"') && inner.endsWith('"')) || (inner.startsWith("'") && inner.endsWith("'"))

  if (isQuoted) return inner.slice(1, -1)
  return inner
}

/**
 * Extracts the function name from a token like `min(10)` → `min`.
 */
export const extractFunctionName = (token: string): string => {
  const parenIdx = token.indexOf('(')
  if (parenIdx === -1) return token
  return token.slice(0, parenIdx).trim()
}
